import { geometry } from 'app/contracts/Geometry';
import { particles } from 'app/contracts/Particles';
import { ParticleBehaviourTagType } from 'app/enum/Particles';
import { ParticleBehaviour } from '../ParticleBehaviour';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';

export class ParticleProgressParallax extends ParticleBehaviour implements particles.ParticlePosition {
	protected _tags: Array<ParticleBehaviourTagType> = [ParticleBehaviourTagType.PROGRESS_PARALLAX];

	public disableTimelineX: boolean = false;

	private _parallaxOffsetPower = 0; //Offset
	private _relativeParallaxXToProgressRatio: number;
	private _dragOffsetX: number; //
	private _targetX: number;
	private _absoluteX: number;
	private _relativePosition: geometry.Vector2 = { x: 0, y: 0 };
	private _xOffset: number = 0;


	private _followParticle: HomeParticle | null = null;
	private _folllowOffsetAngleDegrees: number = 0;
	private _folllowOffsetRadius: number = 0.03;

	//PUBLIC METHODS
	//-----------------------------------------------------------------------------------------
	public set x(x: number) {
		this._relativePosition.x = x;
		this.resize();
	}

	public get x(): number {
		return this._relativePosition.x;
	}

	public set y(y: number) {
		this._relativePosition.y = y;
	}

	public get y(): number {
		return this._relativePosition.y;
	}

	public get absX(): number {
		return this._targetX;
	}

	public get absX2(): number {
		return this._absoluteX;
	}

	public get absY(): number {
		return this._relativePosition.y * this._sceneProps.sizes.container.h;
	}

	public set power(power: number) {
		this._parallaxOffsetPower = power;
	}

	public set effectLerpPerFrame(effect: number) {
	}

	public getAbsX(relativeX: number) {
		let absoulteX: number = this._sceneProps.sizes.container.w * relativeX;
		this._calculateParallaxDragOffsetX(this._sceneProps.progress.ratio);
		return absoulteX + this._sceneProps.progress.containerX + this._dragOffsetX;
	}

	public getAbsY(relativeY: number) {
		return relativeY * this._sceneProps.sizes.container.h;
	}

	public set trackMouse(track: boolean) {
	}

	public set updateTrackMouse(update: boolean) {
	}

	public set xOffset(offset: number) {
		this._xOffset = offset;
	}

	public set followParticle(p: HomeParticle | null) {
		this._followParticle = p;
		this._folllowOffsetAngleDegrees = Math.random() * 360;
		this._folllowOffsetRadius = 0.009;
	}

	public set followRadius(r: number) {
		this._folllowOffsetRadius = r;
	}

	/**
	 * //Set position in abs values if working in pixels rather than timeline %
	 * @param position
	 * @param hardPositionReset
	 */
	public setAbs(position: geometry.Vector2, hardPositionReset: boolean = false): void {
		let relX = position.x / this._sceneProps.sizes.container.w;
		let relY = position.y / this._sceneProps.sizes.container.h;

		this.set({ x: relX, y: relY }, hardPositionReset);
	}

	/** Set position to target - if reset then the ineria will cause the particles to animate to new setting (If using basic motion) */
	public set(position: geometry.Vector2, hardPositionReset: boolean = false): void {
		this._relativePosition = position;
		this.resize();

		if (hardPositionReset) {
			this._calculateParallaxDragOffsetX(this._sceneProps.progress.ratio);

			if (this.disableTimelineX) {
				this._targetX = this._absoluteX;
			} else {
				this._targetX = this._absoluteX + this._sceneProps.progress.containerX + this._dragOffsetX;
			}

			this._animatedProps.setHardX(this._targetX);
			this._animatedProps.setHardY(this._relativePosition.y * this._sceneProps.sizes.container.h);
		}
	}

	public lerp(position: geometry.Vector2, frames: number = 90, onComplete: CallableFunction = null): void {
	}

	public resize(): void {
		this._absoluteX = this._sceneProps.sizes.container.w * this._relativePosition.x;
	}

	public tick(): void {
		if (this.particleIndex == 8440) {
		}

		if (this._followParticle) {

			if (this._folllowOffsetAngleDegrees > 360) {
				this._folllowOffsetAngleDegrees = 0;
			}

			let pointAngleInRadians = this._folllowOffsetAngleDegrees * (Math.PI / 180);
			var xRadiusOffset = Math.cos(pointAngleInRadians) * this._folllowOffsetRadius;
			//Ex globals.virtualYRatio (virtualWidth / virtualHeight) — the same
			//metrics live on sceneProps.sizes: container.w is the virtual width,
			//container.h the virtual height (window height), set per resize:
			var yRadiusOffset =
				Math.sin(pointAngleInRadians) *
				this._folllowOffsetRadius *
				(this._sceneProps.sizes.container.w / this._sceneProps.sizes.container.h);

			this.set({
				x: this._followParticle.position.x + xRadiusOffset,
				y: this._followParticle.position.y + yRadiusOffset
			});
		}

		this._absoluteX = this._sceneProps.sizes.container.w * this._relativePosition.x;

		this._calculateParallaxDragOffsetX(this._sceneProps.progress.ratio);


		//If using X positioning on
		if (this.disableTimelineX) {
			this._targetX = this._absoluteX + this._xOffset;
		} else {
			this._targetX =
				this._absoluteX +
				this._sceneProps.progress.containerX +
				this._dragOffsetX +
				this._xOffset * this._sceneProps.sizes.container.w;
		}

		//When parallax postioning is active on particle it sets the base position the particle will head for
		//Other positional effectors will += the targets
		this._animatedProps.setTargetX(this._targetX);
		this._animatedProps.setTargetY(this._relativePosition.y * this._sceneProps.sizes.container.h);


	}

	/**
	 * Clamp position to current transformed position:
	 * Note all behaviours should be disabled at same time or it will jump
	 */
	public clamp(): void {
		this.set(
			{
				x:
					(-1 * this._sceneProps.progress.containerX + this._particleProps.sprite.x) /
					this._sceneProps.sizes.container.w,
				y: this._particleProps.sprite.y / this._sceneProps.sizes.container.h
			},
			true
		);
	}

	//PRIVATE METHODS
	//-----------------------------------------------------------------------------------------

	//Calculate the parallax offset caused by draging left <> right:
	private _calculateParallaxDragOffsetX(progressRatio: number): void {
		//Translate this into the current progress
		//Correct thing to allow for the fact the start/photoshop point of the timeline is center/0.5/home


		this._relativeParallaxXToProgressRatio = this._relativePosition.x - 0.5 - (progressRatio - 0.5);

		//Set the offset as percentage of the container width (half the width because of the 0.5 logic).
		this._dragOffsetX =
			this._relativeParallaxXToProgressRatio * this._parallaxOffsetPower * (this._sceneProps.sizes.container.w * 0.5);
	}


}
